import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Build-time settings arrive from the hosting dashboard, where a stray quote, space or missing
 * "https://" is easy to paste. Clean them up, and never let a bad value blank the whole site.
 */
function clean(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const s = v
    .trim()
    .replace(/^['"]+|['"]+$/g, '')
    .trim();
  return s || undefined;
}
/** Only a real Supabase project address (or a local one in development) is accepted. */
function origin(v: string | undefined): string | undefined {
  if (!v) return undefined;
  try {
    const u = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`);
    const local = u.hostname === 'localhost' || u.hostname === '127.0.0.1';
    if (!local && !u.hostname.endsWith('.supabase.co')) return undefined;
    if (typeof window !== 'undefined' && u.origin === window.location.origin) return undefined;
    return u.origin;
  } catch {
    return undefined;
  }
}

const rawUrl = clean(import.meta.env['VITE_SUPABASE_URL']);
const url = origin(rawUrl);
const key = clean(import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY']);

function build(): SupabaseClient | null {
  if (!url || !key) return null;
  try {
    return createClient(url, key, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
      },
    });
  } catch (err) {
    console.error('Sign-in could not start', err);
    return null;
  }
}

export const supabase: SupabaseClient | null = build();
export const authConfigured = supabase !== null;
/** Why sign-in is unavailable, for the on-screen message. */
export const authProblem: string | null = authConfigured
  ? null
  : !rawUrl || !key
    ? 'The sign-in settings are missing from this build.'
    : 'The sign-in address in this build is not a valid URL.';

const looksLikeJwt = (t: string | null | undefined): t is string =>
  typeof t === 'string' && t.startsWith('eyJ') && t.split('.').length === 3;

/**
 * The token for API calls. A saved session can go bad in one browser (an interrupted refresh, an
 * old build's leftovers); rather than sending garbage, refresh it, and if that fails drop it so the
 * app shows the sign-in screen instead of an error.
 */
export async function getAccessToken(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  if (!session) return null;
  const fresh = (session.expires_at ?? 0) * 1000 > Date.now() + 30_000;
  if (looksLikeJwt(session.access_token) && fresh) return session.access_token;
  return refreshAccessToken();
}

/** Force a new access token; sign out locally when the session cannot be recovered. */
export async function refreshAccessToken(): Promise<string | null> {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (!error && looksLikeJwt(data.session?.access_token)) return data.session.access_token;
  } catch {
    /* fall through to a clean sign-out */
  }
  await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
  return null;
}

export function onSession(cb: (s: Session | null) => void): () => void {
  if (!supabase) {
    cb(null);
    return () => {};
  }
  void supabase.auth.getSession().then(({ data }) => cb(data.session));
  const { data } = supabase.auth.onAuthStateChange((_e, s) => cb(s));
  return () => data.subscription.unsubscribe();
}

export interface AuthProviders {
  apple: boolean;
  google: boolean;
}

/** Which social logins are switched on in Supabase, so buttons only appear when they work. */
export async function authProviders(): Promise<AuthProviders> {
  const none = { apple: false, google: false };
  if (!url || !key) return none;
  try {
    const res = await fetch(`${url}/auth/v1/settings`, { headers: { apikey: key } });
    if (!res.ok) return none;
    const body = (await res.json()) as { external?: Record<string, boolean> };
    return { apple: body.external?.['apple'] === true, google: body.external?.['google'] === true };
  } catch {
    return none;
  }
}

/** Sign in with Apple through Supabase; returns here with a code the client exchanges itself. */
export async function signInWithApple(returnPath = '/'): Promise<string | null> {
  if (!supabase) return 'Sign-in is not available.';
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'apple',
    options: { redirectTo: `${window.location.origin}${returnPath}` },
  });
  return error ? error.message : null;
}

export async function sendEmailCode(email: string, returnPath = '/'): Promise<string | null> {
  if (!supabase) return 'Sign-in is not available.';
  const { error } = await supabase.auth.signInWithOtp({
    email: email.trim(),
    options: { shouldCreateUser: true, emailRedirectTo: `${window.location.origin}${returnPath}` },
  });
  return error ? error.message : null;
}

export async function verifyEmailCode(email: string, code: string): Promise<string | null> {
  if (!supabase) return 'Sign-in is not available.';
  const { error } = await supabase.auth.verifyOtp({
    email: email.trim(),
    token: code.trim(),
    type: 'email',
  });
  if (!error) return null;
  return /expired|invalid/i.test(error.message)
    ? 'That code did not match. Use the newest email and try again.'
    : error.message;
}

export async function signInWithPassword(email: string, password: string): Promise<string | null> {
  if (!supabase) return 'Sign-in is not available.';
  const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
  return error ? error.message : null;
}

export async function signOut(): Promise<void> {
  await supabase?.auth.signOut();
}

export interface SignUpResult {
  error: string | null;
  needsConfirmation: boolean;
}

/** Create a login with email and password. Supabase emails a confirmation link unless auto-confirm is on. */
export async function signUpWithPassword(
  input: { email: string; password: string; firstName?: string; lastName?: string },
  returnPath = '/',
): Promise<SignUpResult> {
  if (!supabase) return { error: 'Sign-in is not available.', needsConfirmation: false };
  const data: Record<string, string> = {};
  if (input.firstName?.trim()) data['first_name'] = input.firstName.trim();
  if (input.lastName?.trim()) data['last_name'] = input.lastName.trim();
  const { data: result, error } = await supabase.auth.signUp({
    email: input.email.trim(),
    password: input.password,
    options: { emailRedirectTo: `${window.location.origin}${returnPath}`, data },
  });
  if (error) return { error: error.message, needsConfirmation: false };
  // With confirmations on, Supabase answers an existing email with a user that has no identities.
  if (result.user && (result.user.identities?.length ?? 0) === 0)
    return {
      error: 'An account with this email already exists. Sign in, or use Forgot password.',
      needsConfirmation: false,
    };
  return { error: null, needsConfirmation: !result.session };
}

export async function resendConfirmation(email: string, returnPath = '/'): Promise<string | null> {
  if (!supabase) return 'Sign-in is not available.';
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: email.trim(),
    options: { emailRedirectTo: `${window.location.origin}${returnPath}` },
  });
  return error ? error.message : null;
}

export async function requestPasswordReset(email: string): Promise<string | null> {
  if (!supabase) return 'Sign-in is not available.';
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  return error ? error.message : null;
}

export interface PasswordUpdate {
  error: string | null;
  /** Supabase wants a fresh emailed code before changing the password (secure password change). */
  needsCode: boolean;
}

export async function updatePassword(password: string, nonce?: string): Promise<PasswordUpdate> {
  if (!supabase) return { error: 'Sign-in is not available.', needsCode: false };
  const { error } = await supabase.auth.updateUser(nonce ? { password, nonce } : { password });
  if (!error) return { error: null, needsCode: false };
  const code = (error as { code?: string }).code;
  if (code === 'reauthentication_needed' || /reauthenticat/i.test(error.message))
    return { error: null, needsCode: true };
  if (code === 'same_password')
    return { error: 'That is already your password.', needsCode: false };
  return { error: error.message, needsCode: false };
}

/** Emails a one-time code that confirms a sensitive change. */
export async function sendReauthCode(): Promise<string | null> {
  if (!supabase) return 'Sign-in is not available.';
  const { error } = await supabase.auth.reauthenticate();
  return error ? error.message : null;
}

export async function updateEmail(email: string, returnPath = '/'): Promise<string | null> {
  if (!supabase) return 'Sign-in is not available.';
  const { error } = await supabase.auth.updateUser(
    { email: email.trim() },
    { emailRedirectTo: `${window.location.origin}${returnPath}` },
  );
  return error ? error.message : null;
}

export async function signOutEverywhere(): Promise<void> {
  await supabase?.auth.signOut({ scope: 'global' });
}
